import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../../../../store/store';
import useGoogleTTS from '../useGoogleTTS';
import { GOOGLE_TTS_MODEL, GoogleTtsModel } from '../types';
import { buildGoogleSynthesisRequest, getGoogleTtsInputLimitError } from '../utils/googleTtsRequest';
import useGoogleVoiceCatalog from './useGoogleVoiceCatalog';
import useGoogleVoiceDraft from './useGoogleVoiceDraft';

interface UseGoogleAiVoiceControllerArgs {
  onSelect?: (aSrc: string, aFileName?: string) => void;
  onLoadingChange?: (loading: boolean) => void;
}

const normalizeLanguageCode = (value: string) => value.trim().replace(/_/g, '-').toLowerCase();

const useGoogleAiVoiceController = ({ onSelect, onLoadingChange }: UseGoogleAiVoiceControllerArgs) => {
  const { mediaLibraryStore, pageStore, courseStore, propertyStore } = useStore();
  const { uploadFileToFolder } = mediaLibraryStore;
  const { coursePage } = pageStore;
  const { pageProperties } = courseStore;

  const [catalogModel, setCatalogModel] = useState<GoogleTtsModel>(GOOGLE_TTS_MODEL);
  const [uploading, setUploading] = useState(false);
  const generateInFlightRef = useRef(false);
  const applyInFlightRef = useRef(false);

  const { status, retrying, voiceError, voicesByLanguage, languageOptions, getVoicesForLanguage, refreshCatalog } = useGoogleVoiceCatalog(catalogModel);
  const {
    draft,
    loadingDraft,
    draftError,
    normalizedDraft,
    hasTextToGenerate,
    singleSpeakerText,
    setDraftError,
    setPrompt,
    setPromptSpeakerA,
    setPromptSpeakerB,
    setModel: setDraftModel,
    setLanguageCode,
    setLanguageCodeWithoutRemember,
    setMultiSpeaker,
    setSpeakerA,
    setSpeakerB,
    setSingleSpeakerText,
    updateSegment,
    deleteSegment,
    addSegment,
  } = useGoogleVoiceDraft({
    propertyStore,
    voicesByLanguage,
    canReconcileWithCatalog: status === 'ready',
  });

  useEffect(() => {
    if (loadingDraft) return;
    if (draft.model === catalogModel) return;
    setCatalogModel(draft.model);
  }, [catalogModel, draft.model, loadingDraft]);

  const handleModelChange = useCallback(
    (model: GoogleTtsModel) => {
      setDraftModel(model);
      setCatalogModel(model);
    },
    [setDraftModel],
  );

  const { fetchGoogleTTS, audioFile, audioUrl, audioDuration, loading, error, clearError } = useGoogleTTS();

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  useEffect(
    () => () => {
      onLoadingChange?.(false);
    },
    [onLoadingChange],
  );

  const selectedLanguage =
    languageOptions.find((language) => normalizeLanguageCode(language.code) === normalizeLanguageCode(draft.languageCode)) ?? languageOptions[0];

  const languageCode = draft.languageCode || languageOptions[0]?.code;
  const voiceOptions = useMemo(() => getVoicesForLanguage(languageCode), [languageCode, getVoicesForLanguage]);
  const selectedVoiceA = voiceOptions.find((voice) => voice.voiceId === draft.speakers.speakerA.voiceId) ?? voiceOptions[0];
  const selectedVoiceB = voiceOptions.find((voice) => voice.voiceId === draft.speakers.speakerB.voiceId) ?? voiceOptions[Math.min(1, voiceOptions.length - 1)];

  useEffect(() => {
    if (loadingDraft || status !== 'ready') return;
    if (draft.languageCode.trim().length > 0) return;
    if (!selectedLanguage || voiceOptions.length < 1) return;

    setLanguageCodeWithoutRemember(selectedLanguage.code);
  }, [draft.languageCode, loadingDraft, selectedLanguage, setLanguageCodeWithoutRemember, status, voiceOptions.length]);

  const generationDraft = useMemo(() => {
    if (!normalizedDraft.multiSpeaker) return normalizedDraft;

    const promptParts: string[] = [];
    const promptSpeakerA = (normalizedDraft.promptSpeakerA ?? '').trim();
    const promptSpeakerB = (normalizedDraft.promptSpeakerB ?? '').trim();
    const globalPrompt = (normalizedDraft.prompt ?? '').trim();

    if (promptSpeakerA.length > 0 || promptSpeakerB.length > 0 || globalPrompt.length > 0) {
      promptParts.push('Voice style instructions (do not read these instructions aloud).');
      if (promptSpeakerA.length > 0) promptParts.push(`Alias A style: ${promptSpeakerA}`);
      if (promptSpeakerB.length > 0) promptParts.push(`Alias B style: ${promptSpeakerB}`);
      if (globalPrompt.length > 0) promptParts.push(`Overall style: ${globalPrompt}`);
      promptParts.push('Hard constraint: keep each turn on its declared alias. Do not swap, merge, or omit speakers.');
      promptParts.push('Speak only the dialogue turns from the script.');
    }

    return {
      ...normalizedDraft,
      prompt: promptParts.length > 0 ? promptParts.join('\n') : undefined,
      segments: normalizedDraft.segments.map((segment) => ({
        ...segment,
        text: segment.text,
      })),
    };
  }, [normalizedDraft]);

  const generationRequestPayload = useMemo(() => buildGoogleSynthesisRequest(generationDraft), [generationDraft]);
  const validationError = useMemo(() => getGoogleTtsInputLimitError(generationRequestPayload), [generationRequestPayload]);

  useEffect(() => {
    if (loading) return;
    clearError();
  }, [clearError, generationDraft, loading]);

  const resolveContext = useCallback(() => {
    let courseID: string | number = 'unknown';
    let pageID: string | number = 'unknown';

    if (coursePage) {
      courseID = coursePage.courseID;
      pageID = coursePage.coursePageID;
    } else if (pageProperties?.descriptions?.items?.title?.option?.value) {
      courseID = pageProperties.courseID;
      pageID = pageProperties.coursePageID;
    }

    return { courseID, pageID };
  }, [coursePage, pageProperties]);

  const handleGenerateVoice = useCallback(async () => {
    if (generateInFlightRef.current) return;
    if (!hasTextToGenerate || status !== 'ready') return;
    if (validationError) return;
    if (!selectedLanguage || voiceOptions.length < 1) return;

    generateInFlightRef.current = true;
    setDraftError(null);

    try {
      await propertyStore.saveGoogleAIVoiceDraft(draft).catch(() => {
        setDraftError('Unable to save AI Voice draft for this page.');
        return false;
      });

      const context = resolveContext();
      await fetchGoogleTTS(generationDraft, context.courseID, context.pageID, draft).catch(() => {
        setDraftError('Unable to generate audio.');
      });
    } finally {
      generateInFlightRef.current = false;
    }
  }, [draft, fetchGoogleTTS, generationDraft, hasTextToGenerate, propertyStore, resolveContext, selectedLanguage, setDraftError, status, validationError, voiceOptions.length]);

  const handleUploadVoice = useCallback(async () => {
    if (applyInFlightRef.current) return;
    if (audioFile === null || status !== 'ready') return;

    applyInFlightRef.current = true;
    setUploading(true);

    try {
      const result = await uploadFileToFolder(audioFile, 'AIVoice');
      if (!result) return;
      const keys = Object.keys(result);
      if (keys.length < 1 || result[keys[0]].status === false) return;
      onSelect?.(result[keys[0]].url, result[keys[0]].mediaTitle);
    } catch {
      setDraftError('Unable to apply AI Voice to page.');
    } finally {
      applyInFlightRef.current = false;
      setUploading(false);
    }
  }, [audioFile, onSelect, setDraftError, status, uploadFileToFolder]);

  return {
    addSegment,
    audioDuration,
    audioFile,
    audioUrl,
    deleteSegment,
    draft,
    draftError,
    error,
    validationError,
    handleGenerateVoice,
    handleModelChange,
    handleUploadVoice,
    hasTextToGenerate,
    languageOptions,
    loading,
    loadingDraft,
    retrying,
    selectedLanguage,
    selectedVoiceA,
    selectedVoiceB,
    setLanguageCode,
    setMultiSpeaker,
    setPrompt,
    setPromptSpeakerA,
    setPromptSpeakerB,
    setSingleSpeakerText,
    setSpeakerA,
    setSpeakerB,
    singleSpeakerText,
    status,
    updateSegment,
    uploading,
    voiceError,
    voiceOptions,
    refreshCatalog,
  };
};

export default useGoogleAiVoiceController;
